import { useEffect, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '../lib/apiClient';

interface LogEntry {
  ts: string;
  level: string;
  msg: string;
  ctx?: Record<string, unknown>;
}

interface LogsResponse {
  entries: LogEntry[];
  capturedSize: number;
  ringMax: number;
}

const LEVEL_CLASS: Record<string, string> = {
  FATAL: 'log-level-error',
  ERROR: 'log-level-error',
  WARN: 'log-level-warning',
  INFO: 'log-level-info',
  DEBUG: 'log-level-debug',
  TRACE: 'log-level-debug',
};

const LEVEL_OPTIONS = ['ALL', 'ERROR', 'WARN', 'INFO', 'DEBUG'] as const;
const LIMIT_OPTIONS = [100, 200, 500, 1000] as const;
const REFRESH_INTERVAL_MS = 5000;

function formatTs(iso: string): string {
  // Render as local time `YYYY-MM-DD HH:MM:SS.mmm` for log-tail readability.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`;
}

function formatCtx(ctx: Record<string, unknown> | undefined): string {
  if (!ctx) return '';
  const entries = Object.entries(ctx);
  if (entries.length === 0) return '';
  return entries
    .map(([k, v]) => `${k}=${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join(' ');
}

export function LogsTab() {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [capturedSize, setCapturedSize] = useState(0);
  const [ringMax, setRingMax] = useState(0);
  const [levelFilter, setLevelFilter] = useState<string>('ALL');
  const [limit, setLimit] = useState<number>(200);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Debounce the search input by 300ms so rapid typing doesn't fire a
  // request per keystroke against /logs/recent.
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(t);
  }, [search]);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);

  // Server-side filter is the cheap pre-filter (level + limit + search). We
  // apply the same search again client-side as the user types so the input
  // feels instant between refresh ticks.
  useEffect(() => {
    if (paused) return;
    let cancelled = false;
    const tick = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (levelFilter !== 'ALL') params.set('level', levelFilter);
        params.set('limit', String(limit));
        if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
        const data = await apiFetch<LogsResponse>(`/logs/recent?${params.toString()}`);
        if (cancelled) return;
        setEntries(data.entries);
        setCapturedSize(data.capturedSize);
        setRingMax(data.ringMax);
        setLastFetched(new Date());
        setForbidden(false);
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.forbidden) {
          setForbidden(true);
          setError('Logs are admin-only.');
        } else {
          setError(e instanceof Error ? e.message : String(e));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void tick();
    if (!autoRefresh) return () => { cancelled = true; };
    const id = window.setInterval(() => { void tick(); }, REFRESH_INTERVAL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [levelFilter, limit, debouncedSearch, autoRefresh, paused]);

  const filtered = useMemo(() => entries, [entries]);

  return (
    <div className="slide-in">
      <div className="flex flex-wrap gap-3 items-center mb-3">
        <span className="font-semibold text-muted-foreground">Application Logs</span>
        <span className="text-xs text-muted-foreground">
          (live tail from API server, in-memory ring of {ringMax || ' - '} entries)
        </span>
        <select
          value={limit}
          onChange={(e) => setLimit(Number(e.target.value))}
          className="border border-border bg-background rounded-lg px-3 py-1.5 text-sm focus:outline-none"
        >
          {LIMIT_OPTIONS.map(n => <option key={n} value={n}>Last {n}</option>)}
        </select>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Filter text…"
          className="border border-border bg-background rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary w-44"
        />
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Auto-refresh (5s)
        </label>
        <button
          onClick={() => setPaused(p => !p)}
          className="text-xs px-2.5 py-1 rounded-full border border-border hover:border-primary transition"
          title={paused ? 'Resume polling' : 'Pause polling'}
        >
          {paused ? '▶ Resume' : '❚❚ Pause'}
        </button>
        <span className="text-xs text-muted-foreground ml-auto">
          {filtered.length} shown · {capturedSize}/{ringMax} captured
          {lastFetched && ` · updated ${lastFetched.toLocaleTimeString()}`}
          {loading && ' · loading…'}
        </span>
      </div>
      <div className="flex gap-2 mb-3 flex-wrap">
        <span className="text-xs text-muted-foreground">Level:</span>
        {LEVEL_OPTIONS.map(lv => (
          <button
            key={lv}
            onClick={() => setLevelFilter(lv)}
            className={`text-xs px-2.5 py-1 rounded-full border transition-all ${levelFilter === lv ? 'bg-blue-700 border-blue-500 text-blue-100' : 'border-gray-600 text-slate-400 hover:border-blue-400 hover:text-slate-200'}`}
          >
            {lv === 'ALL' ? 'All' : lv}
          </button>
        ))}
      </div>
      {error && !forbidden && (
        <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300 mb-3">
          Failed to load logs: {error}
        </div>
      )}
      {forbidden && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300 mb-3">
          Application logs are visible to MSSP admins only.
        </div>
      )}
      <div className="rounded-xl overflow-hidden" style={{ background: '#0f172a', border: '1px solid #334155' }}>
        <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
          <table className="w-full text-xs" style={{ fontFamily: 'var(--app-font-mono)' }}>
            <thead className="sticky top-0">
              <tr style={{ background: '#1e293b', borderBottom: '1px solid #334155' }}>
                <th className="px-3 py-2 text-left text-slate-400 font-semibold whitespace-nowrap w-52">Time</th>
                <th className="px-3 py-2 text-left text-slate-400 font-semibold w-20">Level</th>
                <th className="px-3 py-2 text-left text-slate-400 font-semibold">Message</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((log, i) => {
                const ctxStr = formatCtx(log.ctx);
                return (
                  <tr key={i} className="border-b align-top" style={{ borderColor: '#1e293b' }}>
                    <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap">{formatTs(log.ts)}</td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-bold min-w-[50px] text-center ${LEVEL_CLASS[log.level] || 'log-level-debug'}`}>
                        {log.level}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-slate-200 whitespace-pre-wrap break-all">
                      {log.msg}
                      {ctxStr && (
                        <span className="block text-slate-500 text-[10px] mt-0.5">{ctxStr}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && !loading && !error && (
                <tr><td colSpan={3} className="px-3 py-8 text-center text-slate-500">
                  {forbidden ? 'No access' : 'No matching log entries'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
